import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * BiovolumDialog - Dialog for distributing cell count across size classes
 * 
 * @param {boolean} isOpen - Whether dialog is visible
 * @param {function} onClose - Close dialog callback
 * @param {object} cellData - Current cell data {organismKey, locationKey, value, organism}
 * @param {object} distribution - Current distribution {classId: percentage}
 * @param {function} setDistribution - Update distribution
 * @param {function} onSave - Save callback with calculated data
 */
export default function BiovolumDialog({
  isOpen,
  onClose,
  cellData,
  distribution,
  setDistribution,
  onSave
}) {
  if (!isOpen || !cellData) return null;

  const { organism, locationKey, value } = cellData;
  const classes = organism.biovolume_classes || [];
  
  // Parse cell count
  const cellCount = parseInt(String(value).replace(/[^\d-]/g, '')) || 0;
  
  // Calculate total biovolume
  const calculateTotalBiovolume = () => {
    let total = 0;
    classes.forEach(cls => {
      const classKey = cls.id || cls.name;
      const percentage = (distribution[classKey] || 0) / 100;
      const classCount = cellCount * percentage;
      total += classCount * (cls.biovolume || 0);
    });
    return total;
  };
  
  // Get total percentage
  const totalPercentage = Object.values(distribution).reduce((a, b) => a + (b || 0), 0);
  
  // Update distribution and auto-adjust Median
  const updateDistribution = (classKey, newValue, isMedian) => {
    const medianClass = classes.find(c => 
      c.name.toLowerCase() === 'median' || c.id === 'median'
    );
    const medianKey = medianClass?.id || 'median';
    
    if (isMedian) {
      // If changing Median directly, just set it (capped at 100)
      setDistribution(prev => ({
        ...prev,
        [classKey]: Math.min(100, Math.max(0, newValue))
      }));
    } else {
      // Calculate sum of all non-median classes
      const otherClasses = classes.filter(c => 
        (c.id || c.name) !== medianKey && (c.id || c.name) !== classKey
      );
      const otherSum = otherClasses.reduce((sum, c) => {
        return sum + (distribution[c.id || c.name] || 0);
      }, 0);
      
      // New value for this class (capped)
      const cappedValue = Math.min(100, Math.max(0, newValue));
      
      // Calculate what Median should be to make total = 100
      const medianValue = Math.max(0, 100 - otherSum - cappedValue);
      
      setDistribution(prev => ({
        ...prev,
        [classKey]: cappedValue,
        [medianKey]: medianValue
      }));
    }
  };
  
  const handleSave = () => {
    const totalBiovolume = calculateTotalBiovolume();
    
    onSave({
      distribution,
      totalBiovolume,
      cellCount
    });
    
    toast.success('Biovolum-fordeling lagret!');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-[500px] max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              <span>Biovolum-fordeling</span>
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {organism.species} @ {locationKey}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        </div>
        
        {/* Cell value display */}
        <div className="p-4 bg-purple-50 rounded-xl border border-purple-200 mb-4">
          <div className="text-center">
            <div className="text-sm text-purple-600 font-medium">Celletelling</div>
            <div className="text-2xl font-bold text-purple-900">
              {cellCount.toLocaleString('nb-NO')}
            </div>
            <div className="text-xs text-purple-600">celler/L</div>
          </div>
        </div>
        
        {/* Size class distribution */}
        <div className="space-y-3 mb-4">
          <h4 className="text-sm font-semibold text-slate-700">Fordel på størrelses-klasser</h4>
          
          {classes.map((cls) => {
            const classKey = cls.id || cls.name;
            const isMedian = cls.name.toLowerCase() === 'median' || cls.id === 'median';
            
            return (
              <div 
                key={classKey}
                className={`p-3 rounded-lg border ${
                  isMedian 
                    ? 'bg-purple-50 border-purple-300' 
                    : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className={`font-medium ${isMedian ? 'text-purple-700' : 'text-slate-700'}`}>
                        {cls.name}
                      </span>
                      {isMedian && (
                        <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full">
                          Auto
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Biovolum: {(cls.biovolume || 0).toLocaleString('nb-NO')} µm³
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={distribution[classKey] || 0}
                      onChange={(e) => updateDistribution(classKey, parseInt(e.target.value) || 0, isMedian)}
                      disabled={isMedian}
                      className={`w-12 h-8 text-center text-sm border rounded ${
                        isMedian ? 'bg-purple-100 border-purple-300' : ''
                      }`}
                    />
                    <span className="text-sm text-slate-500 ml-1">%</span>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Total percentage indicator */}
          <div className="p-3 rounded-lg border bg-green-50 border-green-300">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Total:</span>
              <span className="font-bold text-green-700">{totalPercentage}%</span>
            </div>
          </div>
          
          {/* Calculated total biovolume */}
          {totalPercentage > 0 && (
            <div className="p-4 bg-gradient-to-r from-purple-100 to-blue-100 rounded-xl border border-purple-200">
              <div className="text-sm text-purple-700 font-medium mb-2">Beregnet totalt biovolum:</div>
              <div className="text-xl font-bold text-purple-900">
                {calculateTotalBiovolume().toLocaleString('nb-NO')} µm³/L
              </div>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="outline" onClick={onClose}>
            Avbryt
          </Button>
          <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
            <BarChart3 className="w-4 h-4 mr-2" />
            Lagre fordeling
          </Button>
        </div>
      </div>
    </div>
  );
}
